/**
 * WelcomeCard (NC-1052, Spec S3) — tutor-facing participant welcome card.
 *
 * `WelcomeCard` is the print-friendly presentational card for ONE participant.
 * `WelcomeCardDialog` wraps 1..N cards in a dialog with Drucken (print) + Kopieren
 * + Schließen. The password is only shown after a deliberate operator action
 * (the caller fetches it via runsApi.getWelcome) and is never persisted client-side.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { Print as PrintIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { WelcomePayload } from '../services/types';
import { addressHint } from '../utils/connectionHint';

const fmtRange = (p: WelcomePayload): string => {
  const start = p.startDateTimeUtc ? new Date(p.startDateTimeUtc).toLocaleString('de-DE') : null;
  const end = p.endDateTimeUtc
    ? new Date(p.endDateTimeUtc).toLocaleString('de-DE')
    : p.expireOn || null;
  if (start && end) return `${start} – ${end}`;
  if (end) return `bis ${end}`;
  return 'Zeitraum nicht festgelegt';
};

const connectionText = (c: WelcomePayload['connection'], p?: WelcomePayload): string => {
  if (c.addressKind === 'none' || !c.address) {
    // NC-1589: a tutor-only account owns no VM — it logs on to the trainee VMs.
    if (p?.tutorAccess) return 'Keine eigene VM — Anmeldung auf den Teilnehmer-VMs (Adressen auf der Schulungsseite).';
    return 'Verbindungsinfo wird nach der Provisionierung bereitgestellt.';
  }
  return `${c.address}:${c.rdpPort} (RDP)`;
};

/**
 * NC-1663: reachability hint for a private address, or null. Only once there is
 * an address to connect to — before provisioning the card says so instead.
 */
export const connectionHint = (p: WelcomePayload): string | null =>
  p.connection.address ? addressHint(p.connection.addressKind) : null;

/** NC-1589: one line explaining the tutor's admin rights, or null for trainees. */
export const tutorAccessText = (p: WelcomePayload): string | null =>
  p.tutorAccess
    ? `Tutor-Zugang: Administrator auf allen ${p.tutorAccess.vmCount} Teilnehmer-VMs — für Software-Installationen.`
    : null;

/** Copyable one-block summary of the access data for a single participant. */
export const welcomeCardClipboardText = (p: WelcomePayload): string =>
  [
    `Schulung: ${p.course ?? p.runId}`,
    `Zeitraum: ${fmtRange(p)}`,
    `Name: ${p.participant.displayName}`,
    `Login (UPN): ${p.participant.upn}`,
    p.participant.domainLogin ? `Login (Domäne): ${p.participant.domainLogin}` : null,
    `Passwort: ${p.participant.password}`,
    `Verbindung: ${connectionText(p.connection, p)}`,
    connectionHint(p),
    tutorAccessText(p),
    p.materialsHint ? `Unterlagen: ${p.materialsHint}` : null,
  ]
    .filter(Boolean)
    .join('\n');

interface WelcomeCardProps {
  payload: WelcomePayload;
}

/** Presentational, print-friendly card for one participant. */
export const WelcomeCard: React.FC<WelcomeCardProps> = ({ payload: p }) => (
  <Box
    className="welcome-card"
    data-testid="welcome-card"
    sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 3, mb: 2,
      pageBreakInside: 'avoid', breakInside: 'avoid',
    }}
  >
    <Typography variant="h5" gutterBottom>
      Willkommen zur Schulung
    </Typography>
    <Typography variant="subtitle1" color="text.secondary">
      {p.course ?? p.runId}
    </Typography>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {fmtRange(p)}
    </Typography>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" gutterBottom>Zugangsdaten</Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
      <Typography variant="body2" color="text.secondary">Name</Typography>
      <Typography variant="body2">{p.participant.displayName}</Typography>
      <Typography variant="body2" color="text.secondary">Login (UPN)</Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.participant.upn}</Typography>
      {p.participant.domainLogin && (
        <>
          <Typography variant="body2" color="text.secondary">Login (Domäne)</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.participant.domainLogin}</Typography>
        </>
      )}
      <Typography variant="body2" color="text.secondary">Passwort</Typography>
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }} data-testid="welcome-card-password">
        {p.participant.password}
      </Typography>
      <Typography variant="body2" color="text.secondary">VM</Typography>
      <Typography variant="body2">{p.connection.vmName || '—'}</Typography>
      <Typography variant="body2" color="text.secondary">Verbindung</Typography>
      <Typography variant="body2">{connectionText(p.connection, p)}</Typography>
    </Box>

    {connectionHint(p) && (
      <Alert severity="info" sx={{ mt: 2 }} data-testid="welcome-card-address-hint">
        {connectionHint(p)}
      </Alert>
    )}

    {p.tutorAccess && (
      <Alert severity="info" sx={{ mt: 2 }} data-testid="welcome-card-tutor-access">
        {tutorAccessText(p)}
      </Alert>
    )}

    {p.materialsHint && (
      <>
        <Divider sx={{ my: 2 }} />
        <Typography variant="body2" color="text.secondary">{p.materialsHint}</Typography>
      </>
    )}
  </Box>
);

interface WelcomeCardDialogProps {
  open: boolean;
  payloads: WelcomePayload[];
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onCopy?: (text: string) => void;
}

/** Dialog rendering 1..N welcome cards with Drucken + (single) Kopieren + Schließen. */
export const WelcomeCardDialog: React.FC<WelcomeCardDialogProps> = ({
  open, payloads, loading = false, error = null, onClose, onCopy,
}) => {
  const handlePrint = () => window.print();
  const handleCopyAll = () => {
    const text = payloads.map(welcomeCardClipboardText).join('\n\n');
    if (onCopy) onCopy(text);
    else void navigator.clipboard?.writeText(text);
  };

  const title = payloads.length > 1 ? `Willkommens-Karten (${payloads.length})` : 'Willkommens-Karte';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="welcome-dialog-title">
      {/* Print only the cards, not the app chrome. */}
      <style>{`@media print { body * { visibility: hidden !important; } .welcome-print-area, .welcome-print-area * { visibility: visible !important; } .welcome-print-area { position: absolute; top: 0; left: 0; width: 100%; } }`}</style>
      <DialogTitle id="welcome-dialog-title">{title}</DialogTitle>
      <DialogContent className="welcome-print-area">
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Karte wird erstellt…
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : payloads.length === 0 ? (
          <Alert severity="info">Keine Teilnehmer mit Zugangsdaten vorhanden.</Alert>
        ) : (
          payloads.map((p) => <WelcomeCard key={p.participant.sam} payload={p} />)
        )}
      </DialogContent>
      <DialogActions className="welcome-no-print">
        <Button onClick={onClose}>Schließen</Button>
        <Button
          startIcon={<CopyIcon />}
          onClick={handleCopyAll}
          disabled={loading || payloads.length === 0}
        >
          Kopieren
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={loading || payloads.length === 0}
        >
          Drucken
        </Button>
      </DialogActions>
    </Dialog>
  );
};
